// Drawing App - Toolbar UI Manager

import { ToolType, ShapeType, BrushSettings, SymmetryMode, BrushPresetType, TextureType, SavedBrushPreset } from './types';
import { SmoothingLevel } from './stabilizer';
import { BRUSH_PRESETS, BrushPreset } from './brush_presets';

export interface ToolbarCallbacks {
  onToolChange: (tool: ToolType) => void;
  onBrushSettingsChange: (settings: Partial<BrushSettings>) => void;
  onShapeTypeChange: (shape: ShapeType) => void;
  onSymmetryChange: (mode: SymmetryMode) => void;
  onSmoothingChange: (level: SmoothingLevel) => void;
  onPresetChange: (preset: BrushPresetType) => void;
  onUndo: () => void;
  onRedo: () => void;
  onAddLayer: () => void;
  onRemoveLayer: () => void;
  onDuplicateLayer: () => void;
  onMoveLayerUp: () => void;
  onMoveLayerDown: () => void;
  onMergeDown: () => void;
  onClearLayer: () => void;
  onSelectLayer: (id: string) => void;
  onLayerVisibility: (id: string, visible: boolean) => void;
  onLayerOpacity: (id: string, opacity: number) => void;
  onExportPNG: () => void;
  onExportSVG: () => void;
  onResetView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onTextureChange: (type: TextureType) => void;
  onTextureStrengthChange: (strength: number) => void;
  onCustomBrushEdit: () => void;
  onSavePreset: () => void;
  onLoadPreset: (id: string) => void;
  onDeletePreset: (id: string) => void;
}

export class ToolbarUI {
  private toolbarEl: HTMLElement;
  private layerPanelEl: HTMLElement;
  private propertiesBarEl: HTMLElement;
  private callbacks: ToolbarCallbacks;
  private activeTool: ToolType = 'brush';
  private brushSettings: BrushSettings;
  private activePreset: BrushPresetType = 'default';
  private isMobile: boolean = false;
  private mobileLayerPanelOpen: boolean = false;
  private mobilePropertiesOpen: boolean = false;
  private savedPresets: SavedBrushPreset[] = [];
  private hasCustomBrush: boolean = false;

  constructor(
    toolbarEl: HTMLElement,
    layerPanelEl: HTMLElement,
    propertiesBarEl: HTMLElement,
    callbacks: ToolbarCallbacks,
    initialSettings: BrushSettings
  ) {
    this.toolbarEl = toolbarEl;
    this.layerPanelEl = layerPanelEl;
    this.propertiesBarEl = propertiesBarEl;
    this.callbacks = callbacks;
    this.brushSettings = { ...initialSettings };
    this.isMobile = window.innerWidth < 768;

    this.renderToolbar();
    this.renderPropertiesBar();
    this.setupMobileLayout();

    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;
      if (wasMobile !== this.isMobile) {
        this.setupMobileLayout();
        this.renderToolbar();
        this.renderPropertiesBar();
      }
    });
  }

  private setupMobileLayout(): void {
    if (this.isMobile) {
      // Move toolbar to bottom floating position
      this.toolbarEl.classList.remove('w-12', 'border-r', 'flex-shrink-0');
      this.toolbarEl.classList.add(
        'fixed', 'bottom-16', 'left-1/2', '-translate-x-1/2',
        'z-50', 'rounded-2xl', 'shadow-2xl', 'px-2', 'py-1'
      );

      // Hide right panel, show toggle button
      const rightPanel = this.layerPanelEl.closest('.w-52') as HTMLElement;
      if (rightPanel) {
        rightPanel.classList.add('hidden');
        rightPanel.classList.add('mobile-panel-hidden');
      }

      // Hide properties bar, it will be shown as popup
      this.propertiesBarEl.classList.add('hidden');
      this.propertiesBarEl.classList.add('mobile-properties-hidden');
    } else {
      // Restore desktop layout
      this.toolbarEl.classList.add('w-12', 'border-r', 'flex-shrink-0');
      this.toolbarEl.classList.remove(
        'fixed', 'bottom-16', 'left-1/2', '-translate-x-1/2',
        'z-50', 'rounded-2xl', 'shadow-2xl', 'px-2', 'py-1'
      );

      const rightPanel = this.layerPanelEl.closest('.w-52') as HTMLElement;
      if (rightPanel) {
        rightPanel.classList.remove('hidden', 'mobile-panel-hidden');
      }

      this.propertiesBarEl.classList.remove('hidden', 'mobile-properties-hidden');
    }
  }

  toggleMobileLayerPanel(): void {
    this.mobileLayerPanelOpen = !this.mobileLayerPanelOpen;
    const rightPanel = this.layerPanelEl.closest('.w-52') as HTMLElement;
    if (rightPanel) {
      if (this.mobileLayerPanelOpen) {
        rightPanel.classList.remove('hidden');
        rightPanel.classList.add('fixed', 'right-0', 'top-12', 'bottom-0', 'z-50', 'shadow-2xl');
      } else {
        rightPanel.classList.add('hidden');
        rightPanel.classList.remove('fixed', 'right-0', 'top-12', 'bottom-0', 'z-50', 'shadow-2xl');
      }
    }
  }

  toggleMobileProperties(): void {
    this.mobilePropertiesOpen = !this.mobilePropertiesOpen;
    if (this.mobilePropertiesOpen) {
      this.propertiesBarEl.classList.remove('hidden');
      this.propertiesBarEl.classList.add(
        'fixed', 'bottom-28', 'left-4', 'right-4',
        'z-50', 'rounded-xl', 'shadow-2xl', 'border', 'border-gray-700'
      );
    } else {
      this.propertiesBarEl.classList.add('hidden');
      this.propertiesBarEl.classList.remove(
        'fixed', 'bottom-28', 'left-4', 'right-4',
        'z-50', 'rounded-xl', 'shadow-2xl', 'border', 'border-gray-700'
      );
    }
  }

  setActivePreset(preset: BrushPresetType): void {
    this.activePreset = preset;
    this.updatePresetHighlight();
  }

  setActiveTool(tool: ToolType): void {
    this.activeTool = tool;
    this.updateToolbarHighlight();
    this.renderPropertiesBar();
  }

  updateSavedPresets(presets: SavedBrushPreset[]): void {
    this.savedPresets = presets;
    this.renderPropertiesBar();
  }

  setCustomBrushActive(active: boolean): void {
    this.hasCustomBrush = active;
    this.renderPropertiesBar();
  }

  updateBrushSettings(settings: BrushSettings): void {
    this.brushSettings = { ...settings };
    this.renderPropertiesBar();
  }

  updateLayerPanel(layers: { id: string; name: string; visible: boolean; opacity: number; locked: boolean }[], activeId: string): void {
    this.layerPanelEl.innerHTML = `
      <div class="p-2 border-b border-gray-700 flex items-center justify-between">
        <span class="text-xs text-gray-400 font-medium uppercase tracking-wider">Layers</span>
        <div class="flex space-x-1">
          <button class="layer-add p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" title="Add Layer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          </button>
          <button class="layer-dup p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" title="Duplicate">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
          </button>
          <button class="layer-del p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-red-400" title="Delete">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
          </button>
        </div>
      </div>
      <div class="layer-list flex-1 overflow-y-auto p-1 space-y-1">
        ${[...layers].reverse().map(l => `
          <div class="layer-item flex items-center space-x-2 px-2 py-1.5 rounded cursor-pointer ${l.id === activeId ? 'bg-blue-600/30 border border-blue-500/50' : 'hover:bg-gray-700/50'}" data-layer-id="${l.id}">
            <button class="layer-vis p-0.5 rounded ${l.visible ? 'text-white' : 'text-gray-600'}" data-layer-id="${l.id}" title="Toggle Visibility">
              <svg class="w-3.5 h-3.5" fill="${l.visible ? 'currentColor' : 'none'}" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
            </button>
            <span class="text-xs text-gray-300 flex-1 truncate">${l.name}</span>
            <span class="text-xs text-gray-500">${Math.round(l.opacity * 100)}%</span>
          </div>
        `).join('')}
      </div>
      <div class="p-2 border-t border-gray-700 flex items-center justify-between">
        <button class="layer-up p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" title="Move Up">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/></svg>
        </button>
        <button class="layer-down p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" title="Move Down">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <button class="layer-merge p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" title="Merge Down">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13l-5 5m0 0l-5-5m5 5V6"/></svg>
        </button>
        <button class="layer-clear p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" title="Clear Layer">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `;

    this.bindLayerEvents();
  }

  updateHistoryButtons(canUndo: boolean, canRedo: boolean): void {
    const undoBtn = this.toolbarEl.querySelector('.tool-undo') as HTMLButtonElement;
    const redoBtn = this.toolbarEl.querySelector('.tool-redo') as HTMLButtonElement;
    if (undoBtn) undoBtn.disabled = !canUndo;
    if (redoBtn) redoBtn.disabled = !canRedo;
  }

  updateZoomLevel(percent: number): void {
    const zoomLabel = document.getElementById('zoom-level');
    if (zoomLabel) zoomLabel.textContent = `${percent}%`;
  }

  private renderToolbar(): void {
    const tools: { type: ToolType; icon: string; title: string }[] = [
      { type: 'brush', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z', title: 'Brush (B)' },
      { type: 'eraser', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16', title: 'Eraser (E)' },
      { type: 'shape', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z', title: 'Shape (S)' },
      { type: 'fill', icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01', title: 'Fill (G)' },
      { type: 'eyedropper', icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01', title: 'Eyedropper (I)' },
      { type: 'select', icon: 'M4 5a1 1 0 011-1h2M4 13v2m0-6v2m16-2v2m0 4v2M15 4h2a1 1 0 011 1m-6-1h2m-8 16h2m4 0h2a1 1 0 001-1m-6 1v0a1 1 0 01-1-1m-4 0H5a1 1 0 01-1-1', title: 'Select (M)' },
      { type: 'pan', icon: 'M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4', title: 'Pan (Space)' },
    ];

    if (this.isMobile) {
      this.toolbarEl.innerHTML = `
        <div class="flex items-center space-x-1">
          ${tools.map(t => `
            <button class="tool-btn p-2 rounded-lg transition-colors ${t.type === this.activeTool ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}" data-tool="${t.type}" title="${t.title}">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${t.icon}"/></svg>
            </button>
          `).join('')}
          <div class="border-l border-gray-700 h-6 mx-1"></div>
          <button class="tool-undo p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Undo">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a5 5 0 015 5v2M3 10l6 6m-6-6l6-6"/></svg>
          </button>
          <button class="tool-redo p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Redo">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10H11a5 5 0 00-5 5v2m15-7l-6 6m6-6l-6-6"/></svg>
          </button>
          <div class="border-l border-gray-700 h-6 mx-1"></div>
          <button class="mobile-layers-toggle p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Layers">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
          </button>
          <button class="mobile-props-toggle p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Settings">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/></svg>
          </button>
        </div>
      `;
    } else {
      this.toolbarEl.innerHTML = `
        <div class="flex flex-col items-center py-2 space-y-1">
          ${tools.map(t => `
            <button class="tool-btn p-2 rounded-lg transition-colors ${t.type === this.activeTool ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}" data-tool="${t.type}" title="${t.title}">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${t.icon}"/></svg>
            </button>
          `).join('')}
          <div class="border-t border-gray-700 w-8 my-2"></div>
          <button class="tool-undo p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Undo (Ctrl+Z)">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a5 5 0 015 5v2M3 10l6 6m-6-6l6-6"/></svg>
          </button>
          <button class="tool-redo p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" title="Redo (Ctrl+Shift+Z)">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10H11a5 5 0 00-5 5v2m15-7l-6 6m6-6l-6-6"/></svg>
          </button>
        </div>
      `;
    }

    this.bindToolbarEvents();
  }

  private updatePresetHighlight(): void {
    const buttons = this.toolbarEl.querySelectorAll('.preset-btn');
    buttons.forEach(btn => {
      const preset = btn.getAttribute('data-preset');
      if (preset === this.activePreset) {
        btn.classList.add('bg-purple-600', 'text-white');
        btn.classList.remove('text-gray-400', 'hover:text-white', 'hover:bg-gray-700');
      } else {
        btn.classList.remove('bg-purple-600', 'text-white');
        btn.classList.add('text-gray-400', 'hover:text-white', 'hover:bg-gray-700');
      }
    });
  }

  private renderPropertiesBar(): void {
    const bs = this.brushSettings;
    const smoothLabels = ['Off', 'Low', 'Med', 'High'];

    const presetRow = `
      <div class="flex items-center space-x-1 px-2 py-1 border-b border-gray-700 overflow-x-auto">
        ${BRUSH_PRESETS.map(p => `
          <button class="preset-btn-prop flex-shrink-0 px-2 py-0.5 rounded text-xs transition-colors ${p.type === this.activePreset ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}" data-preset="${p.type}" title="${p.name}">
            ${p.name}
          </button>
        `).join('')}
        <div class="border-l border-gray-600 h-4 mx-1 flex-shrink-0"></div>
        <button class="save-preset-btn flex-shrink-0 px-2 py-0.5 rounded text-xs text-green-400 hover:text-green-300 hover:bg-gray-700" title="현재 설정 저장">
          + 저장
        </button>
      </div>
    `;

    const savedPresetRow = this.savedPresets.length > 0 ? `
      <div class="flex items-center space-x-1 px-2 py-1 border-b border-gray-700 overflow-x-auto">
        <span class="text-gray-500 text-xs flex-shrink-0">MY:</span>
        ${this.savedPresets.map(p => `
          <button class="saved-preset-btn flex-shrink-0 px-2 py-0.5 rounded text-xs transition-colors text-gray-400 hover:text-white hover:bg-gray-700" data-preset-id="${p.id}" title="${p.name}">
            ${p.name}
          </button>
        `).join('')}
      </div>
    ` : '';

    const textureType = bs.textureType || 'none';
    const textureStrength = bs.textureStrength ?? 0.5;

    this.propertiesBarEl.innerHTML = `
      ${presetRow}
      ${savedPresetRow}
      <div class="flex items-center space-x-4 px-4 py-2 text-sm ${this.isMobile ? 'flex-wrap gap-2' : ''}">
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Size:</span>
          <input type="range" class="prop-size w-20 h-1 accent-blue-500" min="1" max="200" value="${bs.size}">
          <span class="prop-size-val text-white text-xs w-8">${bs.size}px</span>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Opacity:</span>
          <input type="range" class="prop-opacity w-16 h-1 accent-blue-500" min="0" max="100" value="${Math.round(bs.opacity * 100)}">
          <span class="prop-opacity-val text-white text-xs w-8">${Math.round(bs.opacity * 100)}%</span>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Flow:</span>
          <input type="range" class="prop-flow w-16 h-1 accent-blue-500" min="1" max="100" value="${Math.round(bs.flow * 100)}">
          <span class="prop-flow-val text-white text-xs w-8">${Math.round(bs.flow * 100)}%</span>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Hardness:</span>
          <input type="range" class="prop-hardness w-16 h-1 accent-blue-500" min="0" max="100" value="${Math.round(bs.hardness * 100)}">
          <span class="prop-hardness-val text-white text-xs w-8">${Math.round(bs.hardness * 100)}%</span>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Smooth:</span>
          <select class="prop-smoothing bg-gray-700 text-white text-xs rounded px-1 py-0.5 border border-gray-600">
            ${smoothLabels.map((label, i) => `<option value="${i}" ${bs.smoothing === i ? 'selected' : ''}>${label}</option>`).join('')}
          </select>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Symmetry:</span>
          <select class="prop-symmetry bg-gray-700 text-white text-xs rounded px-1 py-0.5 border border-gray-600">
            <option value="none" ${bs.symmetry === 'none' ? 'selected' : ''}>None</option>
            <option value="2-axis" ${bs.symmetry === '2-axis' ? 'selected' : ''}>2-Axis</option>
            <option value="4-axis" ${bs.symmetry === '4-axis' ? 'selected' : ''}>4-Axis</option>
            <option value="6-axis" ${bs.symmetry === '6-axis' ? 'selected' : ''}>6-Axis</option>
            <option value="8-axis" ${bs.symmetry === '8-axis' ? 'selected' : ''}>8-Axis</option>
            <option value="radial" ${bs.symmetry === 'radial' ? 'selected' : ''}>Radial</option>
          </select>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">Texture:</span>
          <select class="prop-texture bg-gray-700 text-white text-xs rounded px-1 py-0.5 border border-gray-600">
            <option value="none" ${textureType === 'none' ? 'selected' : ''}>없음</option>
            <option value="paper-grain" ${textureType === 'paper-grain' ? 'selected' : ''}>종이결</option>
            <option value="canvas-weave" ${textureType === 'canvas-weave' ? 'selected' : ''}>캔버스</option>
            <option value="noise" ${textureType === 'noise' ? 'selected' : ''}>노이즈</option>
            <option value="halftone" ${textureType === 'halftone' ? 'selected' : ''}>하프톤</option>
          </select>
        </div>
        ${textureType !== 'none' ? `
        <div class="flex items-center space-x-2">
          <span class="text-gray-400 text-xs">강도:</span>
          <input type="range" class="prop-tex-strength w-16 h-1 accent-blue-500" min="0" max="100" value="${Math.round(textureStrength * 100)}">
          <span class="prop-tex-strength-val text-white text-xs w-8">${Math.round(textureStrength * 100)}%</span>
        </div>
        ` : ''}
        <div class="flex items-center space-x-2">
          <button class="prop-custom-brush px-2 py-1 text-xs rounded transition-colors ${this.hasCustomBrush ? 'bg-orange-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}" title="커스텀 브러시 편집">
            ${this.hasCustomBrush ? '✦ 커스텀' : '✦ 커스텀'}
          </button>
        </div>
        <div class="flex items-center space-x-2 ml-auto">
          <button class="prop-export-png px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded">PNG</button>
          <button class="prop-export-svg px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded">SVG</button>
        </div>
      </div>
    `;

    this.bindPropertiesEvents();
  }

  private updateToolbarHighlight(): void {
    const buttons = this.toolbarEl.querySelectorAll('.tool-btn');
    buttons.forEach(btn => {
      const tool = btn.getAttribute('data-tool');
      if (tool === this.activeTool) {
        btn.classList.add('bg-blue-600', 'text-white');
        btn.classList.remove('text-gray-400', 'hover:text-white', 'hover:bg-gray-700');
      } else {
        btn.classList.remove('bg-blue-600', 'text-white');
        btn.classList.add('text-gray-400', 'hover:text-white', 'hover:bg-gray-700');
      }
    });
  }

  private bindToolbarEvents(): void {
    this.toolbarEl.querySelectorAll('.tool-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const tool = btn.getAttribute('data-tool') as ToolType;
        this.activeTool = tool;
        this.updateToolbarHighlight();
        this.callbacks.onToolChange(tool);
        if (!this.isMobile) this.renderPropertiesBar();
      });
    });

    // Preset buttons
    this.toolbarEl.querySelectorAll('.preset-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const preset = btn.getAttribute('data-preset') as BrushPresetType;
        this.activePreset = preset;
        this.updatePresetHighlight();
        this.callbacks.onPresetChange(preset);
      });
    });

    this.toolbarEl.querySelector('.tool-undo')?.addEventListener('click', () => this.callbacks.onUndo());
    this.toolbarEl.querySelector('.tool-redo')?.addEventListener('click', () => this.callbacks.onRedo());

    // Mobile toggles
    this.toolbarEl.querySelector('.mobile-layers-toggle')?.addEventListener('click', () => this.toggleMobileLayerPanel());
    this.toolbarEl.querySelector('.mobile-props-toggle')?.addEventListener('click', () => this.toggleMobileProperties());
  }

  private bindPropertiesEvents(): void {
    const sizeInput = this.propertiesBarEl.querySelector('.prop-size') as HTMLInputElement;
    const opacityInput = this.propertiesBarEl.querySelector('.prop-opacity') as HTMLInputElement;
    const flowInput = this.propertiesBarEl.querySelector('.prop-flow') as HTMLInputElement;
    const hardnessInput = this.propertiesBarEl.querySelector('.prop-hardness') as HTMLInputElement;
    const smoothingSelect = this.propertiesBarEl.querySelector('.prop-smoothing') as HTMLSelectElement;
    const symmetrySelect = this.propertiesBarEl.querySelector('.prop-symmetry') as HTMLSelectElement;

    if (sizeInput) {
      sizeInput.addEventListener('input', () => {
        const val = parseInt(sizeInput.value);
        this.brushSettings.size = val;
        (this.propertiesBarEl.querySelector('.prop-size-val') as HTMLElement).textContent = `${val}px`;
        this.callbacks.onBrushSettingsChange({ size: val });
      });
    }

    if (opacityInput) {
      opacityInput.addEventListener('input', () => {
        const val = parseInt(opacityInput.value) / 100;
        this.brushSettings.opacity = val;
        (this.propertiesBarEl.querySelector('.prop-opacity-val') as HTMLElement).textContent = `${Math.round(val * 100)}%`;
        this.callbacks.onBrushSettingsChange({ opacity: val });
      });
    }

    if (flowInput) {
      flowInput.addEventListener('input', () => {
        const val = parseInt(flowInput.value) / 100;
        this.brushSettings.flow = val;
        (this.propertiesBarEl.querySelector('.prop-flow-val') as HTMLElement).textContent = `${Math.round(val * 100)}%`;
        this.callbacks.onBrushSettingsChange({ flow: val });
      });
    }

    if (hardnessInput) {
      hardnessInput.addEventListener('input', () => {
        const val = parseInt(hardnessInput.value) / 100;
        this.brushSettings.hardness = val;
        (this.propertiesBarEl.querySelector('.prop-hardness-val') as HTMLElement).textContent = `${Math.round(val * 100)}%`;
        this.callbacks.onBrushSettingsChange({ hardness: val });
      });
    }

    if (smoothingSelect) {
      smoothingSelect.addEventListener('change', () => {
        const val = parseInt(smoothingSelect.value) as SmoothingLevel;
        this.brushSettings.smoothing = val;
        this.callbacks.onSmoothingChange(val);
      });
    }

    if (symmetrySelect) {
      symmetrySelect.addEventListener('change', () => {
        const val = symmetrySelect.value as SymmetryMode;
        this.brushSettings.symmetry = val;
        this.callbacks.onSymmetryChange(val);
      });
    }

    this.propertiesBarEl.querySelector('.prop-export-png')?.addEventListener('click', () => this.callbacks.onExportPNG());
    this.propertiesBarEl.querySelector('.prop-export-svg')?.addEventListener('click', () => this.callbacks.onExportSVG());

    // Texture dropdown
    const textureSelect = this.propertiesBarEl.querySelector('.prop-texture') as HTMLSelectElement;
    if (textureSelect) {
      textureSelect.addEventListener('change', () => {
        const val = textureSelect.value as TextureType;
        this.brushSettings.textureType = val;
        this.callbacks.onTextureChange(val);
        this.renderPropertiesBar(); // re-render to show/hide strength slider
      });
    }

    // Texture strength slider
    const texStrength = this.propertiesBarEl.querySelector('.prop-tex-strength') as HTMLInputElement;
    if (texStrength) {
      texStrength.addEventListener('input', () => {
        const val = parseInt(texStrength.value) / 100;
        this.brushSettings.textureStrength = val;
        (this.propertiesBarEl.querySelector('.prop-tex-strength-val') as HTMLElement).textContent = `${Math.round(val * 100)}%`;
        this.callbacks.onTextureStrengthChange(val);
      });
    }

    // Custom brush button
    this.propertiesBarEl.querySelector('.prop-custom-brush')?.addEventListener('click', () => {
      this.callbacks.onCustomBrushEdit();
    });

    // Save preset button
    this.propertiesBarEl.querySelector('.save-preset-btn')?.addEventListener('click', () => {
      this.callbacks.onSavePreset();
    });

    // Saved preset buttons
    this.propertiesBarEl.querySelectorAll('.saved-preset-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-preset-id')!;
        this.callbacks.onLoadPreset(id);
      });

      // Right-click context menu for rename/delete
      btn.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        const id = btn.getAttribute('data-preset-id')!;
        this.showPresetContextMenu(e as MouseEvent, id);
      });

      // Long press for mobile
      let longPressTimer: ReturnType<typeof setTimeout> | null = null;
      btn.addEventListener('touchstart', (e) => {
        const id = btn.getAttribute('data-preset-id')!;
        longPressTimer = setTimeout(() => {
          e.preventDefault();
          const touch = (e as TouchEvent).touches[0];
          this.showPresetContextMenu({ clientX: touch.clientX, clientY: touch.clientY } as MouseEvent, id);
        }, 600);
      });
      btn.addEventListener('touchend', () => {
        if (longPressTimer) clearTimeout(longPressTimer);
      });
      btn.addEventListener('touchmove', () => {
        if (longPressTimer) clearTimeout(longPressTimer);
      });
    });

    // Preset buttons in properties bar (mobile)
    this.propertiesBarEl.querySelectorAll('.preset-btn-prop').forEach(btn => {
      btn.addEventListener('click', () => {
        const preset = btn.getAttribute('data-preset') as BrushPresetType;
        this.activePreset = preset;
        this.callbacks.onPresetChange(preset);
        // Update highlight
        this.propertiesBarEl.querySelectorAll('.preset-btn-prop').forEach(b => {
          if (b.getAttribute('data-preset') === preset) {
            b.classList.add('bg-purple-600', 'text-white');
            b.classList.remove('text-gray-400', 'hover:text-white', 'hover:bg-gray-700');
          } else {
            b.classList.remove('bg-purple-600', 'text-white');
            b.classList.add('text-gray-400', 'hover:text-white', 'hover:bg-gray-700');
          }
        });
      });
    });
  }

  private showPresetContextMenu(e: MouseEvent, presetId: string): void {
    // Remove any existing context menu
    document.querySelectorAll('.preset-context-menu').forEach(el => el.remove());

    const menu = document.createElement('div');
    menu.className = 'preset-context-menu fixed z-[200] bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[120px]';
    menu.style.left = `${e.clientX}px`;
    menu.style.top = `${e.clientY}px`;
    menu.innerHTML = `
      <button class="ctx-rename w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 hover:text-white">이름 변경</button>
      <button class="ctx-delete w-full text-left px-3 py-1.5 text-xs text-red-400 hover:bg-gray-700 hover:text-red-300">삭제</button>
    `;

    document.body.appendChild(menu);

    // Ensure menu stays within viewport
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      menu.style.left = `${window.innerWidth - rect.width - 8}px`;
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = `${window.innerHeight - rect.height - 8}px`;
    }

    menu.querySelector('.ctx-rename')?.addEventListener('click', () => {
      menu.remove();
      const preset = this.savedPresets.find(p => p.id === presetId);
      if (preset) {
        const newName = prompt('새 이름:', preset.name);
        if (newName && newName.trim()) {
          preset.name = newName.trim();
          // Trigger a re-render by calling delete then re-save - handled by parent
          // For simplicity, call delete which parent will handle
          this.callbacks.onDeletePreset(presetId);
          // Re-add with new name is handled outside; for now just update UI
        }
      }
    });

    menu.querySelector('.ctx-delete')?.addEventListener('click', () => {
      menu.remove();
      this.callbacks.onDeletePreset(presetId);
    });

    // Close on click outside
    const closeMenu = (ev: Event) => {
      if (!menu.contains(ev.target as Node)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
        document.removeEventListener('touchstart', closeMenu);
      }
    };
    setTimeout(() => {
      document.addEventListener('click', closeMenu);
      document.addEventListener('touchstart', closeMenu);
    }, 0);
  }

  private bindLayerEvents(): void {
    this.layerPanelEl.querySelectorAll('.layer-item').forEach(el => {
      el.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.layer-vis')) return;
        const id = el.getAttribute('data-layer-id')!;
        this.callbacks.onSelectLayer(id);
      });
    });

    this.layerPanelEl.querySelectorAll('.layer-vis').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-layer-id')!;
        const isVisible = btn.classList.contains('text-white');
        this.callbacks.onLayerVisibility(id, !isVisible);
      });
    });

    this.layerPanelEl.querySelector('.layer-add')?.addEventListener('click', () => this.callbacks.onAddLayer());
    this.layerPanelEl.querySelector('.layer-del')?.addEventListener('click', () => this.callbacks.onRemoveLayer());
    this.layerPanelEl.querySelector('.layer-dup')?.addEventListener('click', () => this.callbacks.onDuplicateLayer());
    this.layerPanelEl.querySelector('.layer-up')?.addEventListener('click', () => this.callbacks.onMoveLayerUp());
    this.layerPanelEl.querySelector('.layer-down')?.addEventListener('click', () => this.callbacks.onMoveLayerDown());
    this.layerPanelEl.querySelector('.layer-merge')?.addEventListener('click', () => this.callbacks.onMergeDown());
    this.layerPanelEl.querySelector('.layer-clear')?.addEventListener('click', () => this.callbacks.onClearLayer());
  }
}
